import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  History,
  PlayCircle,
  Download,
  AlertCircle,
  ChevronRight,
  TrendingDown,
  TrendingUp,
  Pencil,
  Check,
  X,
} from 'lucide-react';
import { api, fmt, type System } from '../lib/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmModal from '../components/ConfirmModal';
import ExcelExportButton from '../components/ExcelExportButton';
import QueuedAgentsTable from '../components/QueuedAgentsTable';
import { useToast } from '../contexts/ToastContext';
import { cleanPolicyId } from '../lib/format';
import type { SheetSpec } from '../lib/xlsx-export';

export default function PayRuns() {
  const { sys: sysSlug } = useParams<{ sys: string }>();
  const sys = sysSlug === 'messer' ? 'Messer' : sysSlug === 'empire-aca' ? 'Empire-ACA' : null;

  const q = useQuery({
    queryKey: ['pay-runs', sys],
    queryFn: () => api.listPayRuns(sys as System),
    enabled: !!sys,
  });

  if (!sys) return <div className="alert alert-warning"><p>Pick a system.</p></div>;
  if (q.isLoading) return <LoadingSpinner label="Loading pay runs…" />;

  const runs = q.data?.pay_runs ?? [];
  const openRun = runs.find((r) => r.status === 'open') ?? null;
  // Sort closed runs by closed_at desc — the list endpoint sorts by
  // created_at, which lands backfilled-then-auto-recreated runs in the
  // wrong place (their created_at reflects when we re-processed them, not
  // when the cycle actually was).
  const closedRuns = runs
    .filter((r) => r.status !== 'open')
    .slice()
    .sort((a, b) => (b.closed_at ?? '').localeCompare(a.closed_at ?? ''));

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
          <History size={20} className="inline mr-2" style={{ color: 'var(--color-primary)' }} />
          Pay Runs — {sys}
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
          A pay run auto-opens when you upload your first commission file each cycle. Click the
          open run to review and export.
        </p>
      </div>

      {openRun ? (
        <Link
          to={`/${sysSlug}/pay-runs/${openRun.pay_run_id}`}
          className="card card-hover block"
          style={{ borderLeft: '4px solid var(--color-primary)' }}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p
                className="text-xs uppercase tracking-wider"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <PlayCircle size={12} className="inline mr-1" /> Active Pay Run
              </p>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>
                {openRun.name}
              </h3>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                {openRun.period_label} · started {fmt.date(openRun.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p
                  className="text-xs uppercase tracking-wider"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Paid so far
                </p>
                <p className="text-lg font-semibold font-mono" style={{ color: 'var(--color-text)' }}>
                  {fmt.money(openRun.total)}
                </p>
              </div>
              <span style={{ color: 'var(--color-primary)' }}>→</span>
            </div>
          </div>
        </Link>
      ) : (
        <div className="alert alert-info">
          <AlertCircle size={14} className="inline mr-2" />
          No open pay run right now — drop a commission file in{' '}
          <Link to={`/${sysSlug}/imports`} style={{ color: 'var(--color-primary)' }}>
            Imports
          </Link>{' '}
          and one auto-opens for that month.
        </div>
      )}

      {closedRuns.length > 0 && (
        <div className="card overflow-hidden p-0">
          <div
            className="px-4 py-3 flex items-center justify-between gap-2"
            style={{ borderBottom: '1px solid var(--color-border)' }}
          >
            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
              Closed runs ({closedRuns.length})
            </h3>
            <ExcelExportButton
              filename={`pay-runs-${sys?.toLowerCase() ?? 'all'}.xlsx`}
              small
              sheets={() => [
                {
                  name: 'Pay runs',
                  rows: [
                    ['Name', 'Period', 'Agents', 'Payments', 'Total', 'Closed', 'Status', 'Pay run ID'],
                    ...runs.map((r) => [
                      r.name,
                      r.period_label ?? '',
                      r.agent_count ?? 0,
                      r.payment_count ?? 0,
                      r.total ?? 0,
                      r.closed_at ?? '',
                      r.status,
                      r.pay_run_id,
                    ]),
                  ],
                },
              ]}
            />
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Period</th>
                <th className="text-right">Agents</th>
                <th className="text-right">Payments</th>
                <th className="text-right">Total</th>
                <th>Closed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {closedRuns.map((r) => (
                <tr key={r.pay_run_id}>
                  <td className="font-medium">{r.name}</td>
                  <td className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    {r.period_label}
                  </td>
                  <td className="text-right font-mono">{r.agent_count}</td>
                  <td className="text-right font-mono">{r.payment_count}</td>
                  <td className="text-right font-mono font-semibold">{fmt.money(r.total)}</td>
                  <td className="text-xs">{r.closed_at ? fmt.dateOnly(r.closed_at) : '—'}</td>
                  <td className="text-right">
                    <Link
                      to={`/${sysSlug}/pay-runs/${r.pay_run_id}`}
                      className="text-sm"
                      style={{ color: 'var(--color-primary)' }}
                    >
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function PayRunDetail() {
  const { sys: sysSlug, payRunId } = useParams<{ sys: string; payRunId: string }>();
  const sys = sysSlug === 'messer' ? 'Messer' : sysSlug === 'empire-aca' ? 'Empire-ACA' : null;
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Deep-link to Policy Data with this run + optional agent filter
  const drillTo = (agentId?: string) => {
    const qs = new URLSearchParams();
    if (payRunId) qs.set('pay_run', payRunId);
    if (agentId) qs.set('agent', agentId);
    navigate(`/${sysSlug}/policy-data?${qs.toString()}`);
  };
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [houseExpanded, setHouseExpanded] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [periodDraft, setPeriodDraft] = useState('');
  const [savingName, setSavingName] = useState(false);

  const runQuery = useQuery({
    queryKey: ['pay-run', payRunId],
    queryFn: () => api.getPayRun(payRunId!),
    enabled: !!payRunId,
  });

  // Pay summary is what powers the "what's about to be paid" view while the
  // run is open — payable agents + carried-forward + house combined.
  const summaryQuery = useQuery({
    queryKey: ['pay-summary', sys],
    queryFn: () => api.paySummary(sys as System),
    enabled: !!sys,
  });

  // Lists all runs so we can compute Δ vs the prior closed run. Hoisted
  // above the early returns so the hook count stays stable across renders.
  const allRuns = useQuery({
    queryKey: ['pay-runs', sys],
    queryFn: () => api.listPayRuns(sys as System),
    enabled: !!sys,
  });

  // Fetches the actual rows tagged to this pay run so we can compute the
  // agency-side OVR total (override_amount sum) — Payment records only
  // store agent net.
  const runRowsQuery = useQuery({
    queryKey: ['pay-run-rows', sys, payRunId],
    queryFn: () =>
      api.rowsFiltered({ sys: sys as System, pay_run_id: payRunId! }),
    enabled: !!sys && !!payRunId,
  });

  if (runQuery.isLoading) return <LoadingSpinner label="Loading pay run…" />;
  if (runQuery.isError)
    return (
      <div className="alert alert-error">
        <p>{(runQuery.error as Error).message}</p>
      </div>
    );

  const data = runQuery.data!;
  const summary = summaryQuery.data;
  const isOpen = data.pay_run.status === 'open';
  const payable = summary?.agents ?? [];
  const skipped = summary?.skipped_negative_balance ?? [];
  const houseCombined = summary?.house_combined ?? null;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['pay-run', payRunId] });
    queryClient.invalidateQueries({ queryKey: ['pay-runs'] });
    queryClient.invalidateQueries({ queryKey: ['pay-summary'] });
    queryClient.invalidateQueries({ queryKey: ['payments'] });
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      await api.closePayRun(payRunId!);
      toast.success(`Pay run "${data.pay_run.name}" closed.`);
      setConfirmingClose(false);
      invalidate();
    } catch (err) {
      toast.error(`Close failed: ${(err as Error).message}`);
    } finally {
      setClosing(false);
    }
  };

  const startEditName = () => {
    setNameDraft(data.pay_run.name ?? '');
    setPeriodDraft(data.pay_run.period_label ?? '');
    setEditingName(true);
  };

  const handleSaveName = async () => {
    const name = nameDraft.trim();
    const periodLabel = periodDraft.trim();
    if (!name) {
      toast.error('Name cannot be empty.');
      return;
    }
    const body: { name?: string; period_label?: string } = {};
    if (name !== (data.pay_run.name ?? '')) body.name = name;
    if (periodLabel !== (data.pay_run.period_label ?? '')) body.period_label = periodLabel;
    if (!body.name && !body.period_label) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      await api.renamePayRun(payRunId!, body);
      toast.success('Pay run renamed.');
      setEditingName(false);
      invalidate();
    } catch (err) {
      toast.error(`Rename failed: ${(err as Error).message}`);
    } finally {
      setSavingName(false);
    }
  };

  /**
   * One-button workflow: pay everyone payable, download CSV, then ask if the
   * user wants to close the run. If everyone is already paid, just download
   * the CSV and ask about close.
   */
  const handleExport = async () => {
    if (!sys) return;
    setExporting(true);
    try {
      if (payable.length > 0 || skipped.length > 0) {
        const r = await api.bulkCreatePayments(sys, payRunId!);
        toast.success(
          `Paid ${r.paid_agents} agents · ${fmt.money(summary?.totals.unpaid_net ?? 0)}` +
            (r.held_agents ? ` · ${r.held_agents} held (carried forward)` : ''),
        );
        invalidate();
      }
      try {
        await api.downloadPayRunCsv(payRunId!);
      } catch (err) {
        toast.error(`CSV download failed: ${(err as Error).message}`);
      }
      setConfirmingClose(true);
    } catch (err) {
      toast.error(`Export failed: ${(err as Error).message}`);
    } finally {
      setExporting(false);
    }
  };

  const runRows = runRowsQuery.data?.rows ?? [];

  // Cards: system-wide totals always — Queued + House are "what's
  // currently floating in the system," same regardless of which run page
  // you're on. Run-specific values are Paid this run + Δ vs prior.
  const queuedTotal = summary?.totals.unpaid_net ?? 0;
  const paidTotal = data.summary.total ?? 0;
  const houseTotal = summary?.totals.house_total ?? 0;

  // Agency-side OVR for paid rows in this run — sum override_amount
  const paidOverride = runRows
    .filter((r) => r.payment_id)
    .reduce((s, r) => s + (r.override_amount || 0), 0);

  // Δ vs prior **calendar month's** run. Sorting closed_at desc and
  // taking the next entry breaks down when a month has multiple runs
  // (e.g. April CSV + April backfilled). Compare against the most recent
  // run whose period_label resolves to a strictly-earlier calendar month.
  const periodMonthKey = (label: string | undefined): string | null => {
    if (!label) return null;
    // period_label format from importer/backfill: "<Month> <Year>" (e.g. "April 2026")
    const m = label.match(/^(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})$/);
    if (!m) return null;
    const monthIdx = [
      'january','february','march','april','may','june',
      'july','august','september','october','november','december',
    ].indexOf(m[1].toLowerCase());
    return `${m[2]}-${String(monthIdx + 1).padStart(2, '0')}`; // YYYY-MM
  };
  const thisMonthKey = periodMonthKey(data.pay_run.period_label);
  const priorClosed = (allRuns.data?.pay_runs ?? [])
    .filter((r) => {
      if (r.pay_run_id === payRunId) return false;
      if (r.status === 'open') return false;
      const k = periodMonthKey(r.period_label);
      // Only earlier months. If we can't parse either side, fall back to
      // closed_at-based ordering (covers the rare run with a non-standard
      // period_label).
      if (!thisMonthKey || !k) return true;
      return k < thisMonthKey;
    })
    .sort((a, b) => {
      const ka = periodMonthKey(a.period_label) ?? '';
      const kb = periodMonthKey(b.period_label) ?? '';
      // Most recent month first; tiebreak by closed_at desc within the same month.
      if (kb !== ka) return kb.localeCompare(ka);
      return (b.closed_at ?? '').localeCompare(a.closed_at ?? '');
    })[0];
  const priorTotal = priorClosed?.total ?? null;
  const totalSoFar = paidTotal + queuedTotal;
  const deltaPct =
    priorTotal && priorTotal > 0
      ? Math.round(((totalSoFar - priorTotal) / priorTotal) * 100)
      : null;

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-start gap-3 justify-between flex-wrap">
        <div className="flex items-center gap-3">
          <Link
            to={`/${sysSlug}/pay-runs`}
            className="btn-icon"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            {editingName ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="text"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') setEditingName(false);
                    }}
                    autoFocus
                    disabled={savingName}
                    placeholder="Run name"
                    className="input text-xl font-semibold"
                    style={{ minWidth: 320 }}
                  />
                  {isOpen ? (
                    <span className="badge badge-warning text-xs">Open</span>
                  ) : (
                    <span className="badge badge-success text-xs">Closed</span>
                  )}
                  <button
                    type="button"
                    onClick={handleSaveName}
                    disabled={savingName}
                    className="btn-icon"
                    title="Save"
                    style={{ color: 'var(--color-success, #22c55e)' }}
                  >
                    <Check size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingName(false)}
                    disabled={savingName}
                    className="btn-icon"
                    title="Cancel"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <label
                    className="text-xs uppercase tracking-wider"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    Period
                  </label>
                  <input
                    type="text"
                    value={periodDraft}
                    onChange={(e) => setPeriodDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') setEditingName(false);
                    }}
                    disabled={savingName}
                    placeholder="e.g. April 2026"
                    className="input text-xs"
                    style={{ minWidth: 200 }}
                  />
                </div>
              </div>
            ) : (
              <>
                <h2
                  className="text-2xl font-semibold flex items-center gap-3"
                  style={{ color: 'var(--color-text)' }}
                >
                  {data.pay_run.name}
                  {isOpen ? (
                    <span className="badge badge-warning text-xs">Open</span>
                  ) : (
                    <span className="badge badge-success text-xs">Closed</span>
                  )}
                  <button
                    type="button"
                    onClick={startEditName}
                    className="btn-icon"
                    title="Rename pay run"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    <Pencil size={14} />
                  </button>
                </h2>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  {data.pay_run.period_label} · started {fmt.date(data.pay_run.created_at)}
                  {data.pay_run.closed_at ? ` · closed ${fmt.date(data.pay_run.closed_at)}` : ''}
                </p>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isOpen ? (
            <>
              <button
                onClick={handleExport}
                disabled={exporting}
                className="btn btn-primary text-sm flex items-center gap-2 disabled:opacity-40"
              >
                <Download size={14} />
                {exporting
                  ? 'Working…'
                  : payable.length > 0
                    ? `Pay ${payable.length} agent${payable.length === 1 ? '' : 's'} & download CSV`
                    : 'Download CSV'}
              </button>
              <button
                onClick={() => setConfirmingClose(true)}
                disabled={exporting || closing}
                className="btn btn-secondary text-sm flex items-center gap-2 disabled:opacity-40"
                title="Close this run without paying anything further"
              >
                <Check size={14} /> Close run
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                api
                  .downloadPayRunCsv(payRunId!)
                  .catch((err) =>
                    toast.error(`CSV download failed: ${(err as Error).message}`),
                  );
              }}
              className="btn btn-secondary text-sm flex items-center gap-2"
            >
              <Download size={14} /> Download CSV
            </button>
          )}
          {/* Excel export — full row-by-row breakdown grouped by carrier
              (a sheet per carrier), independent of the accounting CSV. */}
          <ExcelExportButton
            filename={`pay-run-${payRunId ?? ''}.xlsx`}
            sheets={(): SheetSpec[] => {
              // Group rows by carrier
              const byCarrier = new Map<string, typeof runRows>();
              for (const r of runRows) {
                const c = r.carrier || '?';
                if (!byCarrier.has(c)) byCarrier.set(c, []);
                byCarrier.get(c)!.push(r);
              }
              const headers = ['Policy', 'Policyholder', 'Agent', 'Raw', 'OVR', 'Agent net', 'Paid-to date'];
              const carriers = [...byCarrier.keys()].sort();
              const carrierSheets: SheetSpec[] = carriers.map((c) => {
                const list = byCarrier.get(c)!.slice().sort((a, b) => (b.net_amount || 0) - (a.net_amount || 0));
                const data: (string | number | null)[][] = [headers];
                for (const r of list) {
                  data.push([
                    cleanPolicyId(r.policy_id, r.writing_agent_id),
                    r.policyholder_name ?? '',
                    r.agent_name ?? '',
                    r.raw_amount ?? null,
                    r.override_amount ?? null,
                    r.net_amount ?? null,
                    r.paid_to_date ?? r.transaction_date ?? '',
                  ]);
                }
                data.push([
                  `Total · ${list.length} rows`, '', '',
                  list.reduce((s, r) => s + (r.raw_amount || 0), 0),
                  list.reduce((s, r) => s + (r.override_amount || 0), 0),
                  list.reduce((s, r) => s + (r.net_amount || 0), 0),
                  '',
                ]);
                return { name: c, rows: data };
              });
              // Plus a Payments summary sheet at the front
              const paymentSheet: SheetSpec = {
                name: 'Payments',
                rows: [
                  ['Paid at', 'Agent', 'Rows', 'Carriers', 'Total'],
                  ...data.payments.map((p) => [
                    p.paid_at,
                    p.agent_name,
                    p.row_count,
                    Object.entries(p.by_carrier || {}).map(([c, v]) => `${c} (${v.rows})`).join(', '),
                    p.total,
                  ]),
                ],
              };
              return [paymentSheet, ...carrierSheets];
            }}
          />
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Metric
          label="Queued (unpaid)"
          value={fmt.money(queuedTotal)}
          sub={`${summary?.totals.agents ?? 0} agents`}
        />
        <Metric
          label="Paid this run"
          value={fmt.money(paidTotal)}
          sub={
            `${data.summary.payment_count} payments · ${data.summary.agent_count} agents` +
            (paidOverride > 0 ? ` · OVR ${fmt.money(paidOverride)}` : '')
          }
          accent
          onClick={() => drillTo()}
        />
        <Metric
          label="House (system)"
          value={fmt.money(houseTotal)}
          sub={`${summary?.totals.house_agents ?? 0} agents · incl. unmapped`}
          muted
          onClick={() => drillTo('__house__')}
        />
        <Metric
          label="Δ vs prior run"
          value={
            deltaPct === null
              ? '—'
              : `${deltaPct >= 0 ? '+' : ''}${deltaPct}%`
          }
          sub={
            priorClosed
              ? `vs ${fmt.money(priorTotal ?? 0)} (${priorClosed.period_label || priorClosed.name})`
              : 'no prior run'
          }
          icon={
            deltaPct === null ? null : deltaPct >= 0 ? (
              <TrendingUp size={14} />
            ) : (
              <TrendingDown size={14} />
            )
          }
        />
      </div>

      {summary && summary.totals.unattributed_rows > 0 && isOpen && (
        <div className="alert alert-warning flex items-start gap-3">
          <AlertCircle size={16} className="text-amber-500 shrink-0 mt-1" />
          <p className="text-sm">
            <strong>
              {summary.totals.unattributed_rows.toLocaleString()} rows are unattributed
            </strong>{' '}
            — these won't be paid until you map their writing IDs in{' '}
            <Link to={`/${sysSlug}/carriers`} style={{ color: 'var(--color-primary)' }}>
              Carriers
            </Link>
            .
          </p>
        </div>
      )}

      {/* Queued payable agents (when run is open) */}
      {isOpen && payable.length > 0 && (
        <div className="card overflow-hidden p-0">
          <div
            className="px-4 py-3"
            style={{ borderBottom: '1px solid var(--color-border)' }}
          >
            <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
              Queued for payment ({payable.length} agents · {fmt.money(queuedTotal)})
            </h3>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              Click <strong>Pay &amp; download CSV</strong> above to pay all of these and download the
              import file for your accounting tool, or open the <strong>Pay Cycle</strong> page to do both
              books at once.
            </p>
          </div>
          <QueuedAgentsTable agents={payable} />
        </div>
      )}

      {/* Held this run — net ≤ $0 after carry-forward */}
      {isOpen && skipped.length > 0 && (
        <div className="card overflow-hidden p-0">
          <div
            className="px-4 py-3"
            style={{
              borderBottom: '1px solid var(--color-border)',
              backgroundColor: 'rgba(245, 158, 11, 0.06)',
            }}
          >
            <h3
              className="text-sm font-semibold flex items-center gap-2"
              style={{ color: 'var(--color-text)' }}
            >
              <TrendingDown size={14} className="text-amber-500" />
              Held this run ({skipped.length})
            </h3>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              Net ≤ $0 after carry-forward — a $0 statement is created and the balance carries to the
              next run.
            </p>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Agent</th>
                <th className="text-right">Rows</th>
                <th>Per-carrier breakdown</th>
                <th className="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {skipped.map((a) => (
                <tr key={a.agent_id}>
                  <td className="font-medium">{a.agent_name}</td>
                  <td className="text-right font-mono">{a.unpaid_rows}</td>
                  <td className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    {Object.entries(a.by_carrier)
                      .map(([c, v]) => `${c}: ${v.rows} (${fmt.money(v.net)})`)
                      .join(' · ')}
                  </td>
                  <td className="text-right font-mono font-semibold text-amber-500">
                    {fmt.money(a.net_payable ?? a.unpaid_net)}
                    {(a.carried_in ?? 0) < 0 && (
                      <span className="block text-xs font-normal" style={{ color: 'var(--color-text-muted)' }}>
                        incl. {fmt.money(a.carried_in ?? 0)} carried
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* House (combined, expandable) */}
      {isOpen && houseCombined && (
        <div className="card overflow-hidden p-0">
          <button
            type="button"
            onClick={() => setHouseExpanded((v) => !v)}
            className="w-full text-left px-4 py-3 flex items-center justify-between gap-3"
            style={{ borderBottom: '1px solid var(--color-border)', background: 'transparent' }}
          >
            <div className="flex items-center gap-2">
              <ChevronRight
                size={14}
                style={{
                  transform: houseExpanded ? 'rotate(90deg)' : 'none',
                  transition: 'transform 0.15s',
                  color: 'var(--color-text-muted)',
                }}
              />
              <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-muted)' }}>
                House (no payout) · {houseCombined.member_count} agents · {fmt.money(houseTotal)}
              </h3>
            </div>
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {houseCombined.unpaid_rows} rows · per-carrier:{' '}
              {Object.entries(houseCombined.by_carrier)
                .map(([c, v]) => `${c} ${fmt.money(v.net)}`)
                .join(' · ')}
            </span>
          </button>
          {houseExpanded && (
            <table className="table">
              <thead>
                <tr>
                  <th>Underlying agent</th>
                  <th className="text-right">Rows</th>
                  <th>Per-carrier breakdown</th>
                  <th className="text-right">Net</th>
                </tr>
              </thead>
              <tbody>
                {houseCombined.house_members.map((a) => (
                  <tr key={a.agent_id}>
                    <td className="font-medium" style={{ color: 'var(--color-text-muted)' }}>
                      {a.agent_name}
                    </td>
                    <td className="text-right font-mono">{a.unpaid_rows}</td>
                    <td className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      {Object.entries(a.by_carrier)
                        .map(([c, v]) => `${c}: ${v.rows} (${fmt.money(v.net)})`)
                        .join(' · ')}
                    </td>
                    <td className="text-right font-mono">{fmt.money(a.unpaid_net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Already-paid agents (always visible — current Payments in this run) */}
      <div className="card overflow-hidden p-0">
        <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
            {isOpen
              ? `Paid so far in this run (${data.payments.length})`
              : `Payments in this run (${data.payments.length})`}
          </h3>
        </div>
        {data.payments.length === 0 ? (
          <p className="p-6 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {isOpen
              ? 'No payments yet — click Pay & download CSV above to pay the queued agents.'
              : 'No payments in this run.'}
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Paid at</th>
                <th>Agent</th>
                <th className="text-right">Rows</th>
                <th>Carriers</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.payments.map((p) => (
                <tr
                  key={p.payment_id}
                  onClick={() => drillTo(p.agent_id)}
                  title="Click to view policy detail"
                  style={{ cursor: 'pointer' }}
                >
                  <td className="text-sm">{fmt.date(p.paid_at)}</td>
                  <td className="font-medium">{p.agent_name}</td>
                  <td className="text-right font-mono">{p.row_count}</td>
                  <td className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    {Object.entries(p.by_carrier || {})
                      .map(([c, v]) => `${c} (${v.rows})`)
                      .join(', ')}
                  </td>
                  <td className="text-right font-mono font-semibold">{fmt.money(p.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmModal
        open={confirmingClose}
        title={`Close "${data.pay_run.name}"?`}
        message={`This locks the pay run — no more payments can be added. Total ${fmt.money(data.summary.total)} across ${data.summary.payment_count} payments to ${data.summary.agent_count} agents. Notification emails fire on close (SES sandbox: only verified addresses receive).`}
        confirmLabel={closing ? 'Closing…' : 'Close run'}
        variant="warning"
        onConfirm={handleClose}
        onCancel={() => setConfirmingClose(false)}
      />
    </div>
  );
}

function Metric({
  label,
  value,
  sub,
  accent,
  muted,
  icon,
  onClick,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  muted?: boolean;
  icon?: React.ReactNode;
  onClick?: () => void;
}) {
  const interactive = !!onClick;
  return (
    <div
      className={`metric-card ${accent ? 'metric-card-success' : 'metric-card-primary'}`}
      onClick={onClick}
      title={interactive ? 'Click to view policy detail' : undefined}
      style={interactive ? { cursor: 'pointer' } : undefined}
    >
      <div className="flex items-center gap-1 mb-1">
        {icon}
        <p
          className="text-xs uppercase tracking-wider"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {label}
        </p>
      </div>
      <p
        className="text-2xl font-bold"
        style={{ color: muted ? 'var(--color-text-muted)' : 'var(--color-text)' }}
      >
        {value}
      </p>
      {sub && (
        <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
          {sub}
        </p>
      )}
    </div>
  );
}
